import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OverlayService } from '@services/overlay.service';
import { StickyElementService } from '@services/sticky-element.service';
import { ApiService } from '@services/api.service';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, from, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { formatDate } from '@angular/common';
import * as dayjs from 'dayjs';
import {
  ApprovalType,
  EntityType,
  DocumentType,
  EventType,
  GqlService,
  PermissionType,
  WorkflowStep,
  Currency,
} from '@services/gql.service';
import { first, get, isEmpty, merge, now } from 'lodash-es';
import { Utils } from '@services/utils';
import { TrialsQuery } from '@models/trials/trials.query';
import { MainStore } from 'src/app/layouts/main-layout/state/main.store';
import { TableConstants } from 'src/app/constants/table.constants';
import {
  AgCellWrapperComponent,
  getWrapperCellOptions,
} from '@components/ag-cell-wrapper/ag-cell-wrapper.component';
import { BudgetService } from 'src/app/pages/budget-page/tabs/budget-enhanced/state/budget.service';
import { BudgetQuery } from 'src/app/pages/budget-page/tabs/budget-enhanced/state/budget.query';
import { ExcelButtonVariant } from '@components/export-excel-button/export-excel-button.component';
import { AuthService } from '@models/auth/auth.service';
import { OrganizationQuery } from '@models/organization/organization.query';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { ROUTING_PATH } from 'src/app/app-routing-path.const';
import { MainQuery } from 'src/app/layouts/main-layout/state/main.query';
import { DocumentLibraryService } from 'src/app/pages/documents/document-library.service';
import {
  FullCategory,
  FullActivity,
} from 'src/app/pages/forecast-accruals-page/tabs/forecast/category/category.query';
import {
  MonthCloseTableRowData,
  PeriodCloseComponent,
  QuarterDate,
  QuarterStartDataForBottomRow,
  QuarterSummaryData,
} from '../../period-close.component';
import { AddVendorEstimateUploadComponent } from './add-vendor-estimate-upload/add-vendor-estimate-upload.component';
import { AgInMonthGroupHeaderComponent } from './ag-in-month-group-header.component';
import { AgQuarterCloseApprovalComponent } from './ag-quarter-close-approval.component';
import { WorkflowQuery } from './close-quarter-check-list/store';
import { QuarterCloseDialogComponent } from './quarter-close-dialog/quarter-close-dialog.component';
import { QuarterCloseManualOverrideComponent } from './quarter-close-manual-override.component';
import { QuarterCloseProcessingConfirmationHeaderComponent } from './quarter-close-processing-confirmation-header.component';
import { QuarterCloseProcessingConfirmationComponent } from './quarter-close-processing-confirmation.component';
import { VendorEstimateUploadComponent } from './vendor-estimate-upload/vendor-estimate-upload.component';
import {
  GridApi,
  ColumnApi,
  ProcessGroupHeaderForExportParams,
  ProcessCellForExportParams,
  ExcelExportParams,
  ColDef,
  ColGroupDef,
  ValueFormatterParams,
  GridOptions,
  RowClassParams,
  CellValueChangedEvent,
  Column,
  GridReadyEvent,
  FirstDataRenderedEvent,
  IProvidedColumn,
  ITooltipParams,
  ICellRendererParams,
  CellClassParams,
  PostProcessPopupParams,
  FilterChangedEvent,
  CellClickedEvent,
  IRowNode,
  ColumnGroupShowType,
} from '@ag-grid-community/core';
import { AgExpandableGroupHeaderComponent } from '../quarter-close-adjustments/ag-expandable-group-header.component';
import { AgQuarterCloseAdjustmentComponent } from './ag-quarter-close-adjustment.component';
import {
  AgBudgetEnhancedGroupHeaderComponent,
  AgBudgetAttributeComponentParams,
} from '../../../budget-page/tabs/budget-enhanced/ag-budget-enhanced-group-header.component';
import {
  AgQuarterCloseCollapsableHeaderComponent,
  AgQuarterCloseCollapsableHeaderParams,
} from './ag-quarter-close-collapsable-header.component';
import { QuarterCloseChecklistPeriodCloseService } from '../quarter-close-checklist/services/quarter-close-checklist-period-close.service';
import { AddPoReportUploadComponent } from './add-po-report-upload/add-po-report-upload.component';
import { InvoiceService } from 'src/app/pages/vendor-payments-page/tabs/invoices/state/invoice.service';
import { Option } from '@components/components.type';
import { Router } from '@angular/router';
import { AuthQuery } from '@models/auth/auth.query';
import {
  attributeColumnDef,
  calcColumns,
} from '../../../budget-page/tabs/budget-enhanced/column-defs';

// localstorage key for quarter close attributes
const QCAttributesLSKey = 'quarter_close_activities_header';

@UntilDestroy()
@Component({
  selector: 'aux-quarter-close',
  templateUrl: './quarter-close.component.html',
  styleUrls: ['./quarter-close.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuarterCloseComponent implements OnInit, OnDestroy {
  refreshDownload$ = new Subject();

  showGrid$ = new BehaviorSubject(true);

  width$ = new BehaviorSubject(0);

  showRequestJournalEntryButton$: Observable<boolean>;

  isDownloadVendorEstimateButtonDisabled$ = this.refreshDownload$.pipe(
    startWith(null),
    switchMap(() => combineLatest([this.budgetQuery.select(), this.mainQuery.select('trialKey')])),
    switchMap(([{ budget_info }, trialKey]) => {
      const first_vendor_current_month = budget_info[0]?.current_month;

      if (!first_vendor_current_month) {
        return of(0);
      }
      const formatted_current_month = formatDate(first_vendor_current_month, 'MMMM-y', 'en-US');

      return from(
        this.apiService.fileCounts(
          `trials/${trialKey}/vendors/vendor-estimate/${formatted_current_month}`
        )
      ).pipe(map((count) => !count));
    })
  );

  investigatorDetailLink = `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_DETAIL}`;

  gridDataForFilter$ = new BehaviorSubject<MonthCloseTableRowData[]>([]);

  gridAPI$ = new ReplaySubject<GridApi>(1);

  gridAPI!: GridApi;

  gridColumnApi$ = new ReplaySubject<ColumnApi>(1);

  gridColumnApi!: ColumnApi;

  isCloseMonthAvailable$ = combineLatest([
    this.workflowQuery.getMonthCloseButtonAvailability(),
    this.periodCloseComponent.monthClosed$.pipe(map((bool) => !bool)),
  ]).pipe(map((arr) => arr.every((bool) => bool)));

  exportButtonVariant = ExcelButtonVariant.OUTLINE_DOWNLOAD;

  components = {
    approvalRenderer: AgQuarterCloseApprovalComponent,
  };

  postProcessPopup: (params: PostProcessPopupParams) => void = (params: PostProcessPopupParams) => {
    const columnId = params.column ? params.column.getId() : undefined;
    if (columnId === 'account' || columnId === 'dept' || columnId === 'po') {
      const ePopup = params.ePopup;
      let oldTopStr = ePopup.style.top!;
      let oldLeftStr = ePopup.style.left!;
      // remove 'px' from the string (AG Grid uses px positioning)
      oldTopStr = oldTopStr.substring(0, oldTopStr.indexOf('px'));
      oldLeftStr = oldLeftStr.substring(0, oldLeftStr.indexOf('px'));
      const oldTop = parseInt(oldTopStr);
      const oldLeft = parseInt(oldLeftStr);
      const newTop = oldTop + 35;
      const newLeft = oldLeft + 35;
      ePopup.style.top = newTop + 'px';
      ePopup.style.left = newLeft + 'px';
    }
  };

  currencyFormatter = (params: ValueFormatterParams) => {
    let currency = Currency.USD;
    if (params.data === undefined && params.node) {
      currency = this.organizationQuery.getEntity(params.node.key)?.currency || Currency.USD;
    }
    return Utils.agCurrencyFormatterAccounting(
      params,
      params.data?.contract_direct_cost_currency || params.data?.currency || currency
    );
  };

  getCellClass = () => (params: CellClassParams) => {
    if (params?.node?.data?.contract_direct_cost_currency) {
      return [
        `budgetCost${params?.node?.data?.contract_direct_cost_currency}`,
        'ag-cell-align-right',
      ];
    }
    return ['budgetCostNoSymbol', 'ag-cell-align-right'];
  };

  getCellClassForInvoiceCells = () => (params: CellClassParams) => {
    if (params.node.group || params.node.rowPinned === 'bottom') {
      if (params?.node?.data?.contract_direct_cost_currency) {
        return [
          `budgetCost${params?.node?.data?.contract_direct_cost_currency}`,
          'ag-cell-align-right',
          params.value ? 'aux-link' : 'aux-link-zero-hyphen',
          'cursor-pointer',
        ];
      }
      return [
        'budgetCostNoSymbol',
        'ag-cell-align-right',
        params.value ? 'aux-link' : 'aux-link-zero-hyphen',
        'cursor-pointer',
      ];
    } else {
      if (params?.node?.data?.contract_direct_cost_currency) {
        return [
          `budgetCost${params?.node?.data?.contract_direct_cost_currency}`,
          'ag-cell-align-right',
        ];
      }
      return ['budgetCostNoSymbol', 'ag-cell-align-right'];
    }
  };

  excelOptions = {
    sheetName: 'Quarter Close',
    fileName: 'auxilius-quarter-close.xlsx',
    shouldRowBeSkipped(params) {
      return !params.node?.data?.cost_category;
    },
    skipPinnedBottom: true,
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'vendor_id':
          return 280;
        case 'initial_monthly_accrual':
        case 'total_monthly_accrual':
        case 'invoice_amount':
        case 'net_accruals':
          return 170;
        default:
          return 150;
      }
    },
    processGroupHeaderCallback: (params: ProcessGroupHeaderForExportParams): string => {
      const colGroupDef = params.columnGroup.getColGroupDef() || ({} as any);
      const headerName = (colGroupDef.headerName || '') as string;
      if (headerName && headerName.match(/\d+-\d+/)) {
        return dayjs(headerName).format('MMMM YYYY');
      }
      return headerName;
    },
    processCellCallback: (params: ProcessCellForExportParams): string => {
      if (params.column.getColId() === 'vendor_id') {
        return this.organizationQuery.getEntity(params.value)?.name || '';
      }

      return params.value;
    },
  } as ExcelExportParams;

  workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS;

  adjustmentWorkflow$ = this.workflowQuery.getWorkflowByStepType(this.workflowName);

  isQuarterCloseEnabled$ = this.workflowQuery.isQuarterCloseEnabled$;

  isWorkflowAvailable$ = this.workflowQuery.isWorkflowAvailable$;

  userHasClosingPermission$ = this.authService.isAuthorized$({
    sysAdminsOnly: false,
    permissions: [PermissionType.PERMISSION_APPROVE_IN_MONTH],
  });

  userHasAdjustPermission$ = this.authService.isAuthorized$({
    sysAdminsOnly: false,
    permissions: [PermissionType.PERMISSION_MODIFY_OPEN_MONTH_ADJUSTMENTS],
  });

  autoAttributeGroupColumnDef: ColDef = {
    headerName: '',
    minWidth: 250,
    width: 250,
    resizable: true,
    field: 'activity_name',
    tooltipField: 'activity_name',
    headerClass: 'activities-header',
    pinned: 'left',
    headerComponent: AgBudgetEnhancedGroupHeaderComponent,
    headerComponentParams: {
      expandLevel: () => -1,
      template: `Activities`,
      localStorageKey: QCAttributesLSKey,
    } as AgBudgetAttributeComponentParams,
    tooltipValueGetter: (params: ITooltipParams) => {
      return params.node?.group
        ? this.organizationQuery.getEntity(params.value)?.name
        : params.value;
    },
    ...getWrapperCellOptions(),
    cellRendererParams: {
      suppressCount: true,
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    comparator: (_, __, nodeA, nodeB) => {
      if (!nodeA.aggData) {
        return 0;
      }
      return nodeA.aggData.current_lre - nodeB.aggData.current_lre;
    },
  };

  defaultColumns: (ColDef | ColGroupDef)[] = [
    {
      headerName: 'Vendor',
      field: 'vendor_id',
      resizable: false,
      rowGroup: true,
      valueFormatter: (params: ValueFormatterParams) => {
        return this.organizationQuery.getEntity(params.value)?.name || '';
      },
      hide: true,
      cellClass: ['vendor_id'],
    },
    {
      headerName: 'Cost Category',
      field: 'cost_category',
      hide: true,
      cellClass: ['cost_category'],
    },
    TableConstants.SPACER_COLUMN,
    {
      headerName: 'Quarter Start',
      headerClass: ['future', 'ag-header-align-center'],
      headerGroupComponent: AgExpandableGroupHeaderComponent,
      headerGroupComponentParams: {
        collapsedByDefault: true,
        filterCols(column: IProvidedColumn) {
          return !['starting_accrual', 'starting_prepaid'].includes(column.getId());
        },
        expandableCols: ['wp_ltd_start', 'invoice_ltd_start'],
      },
      children: [
        {
          headerName: 'WP LTD',
          maxWidth: 150,
          minWidth: 150,
          resizable: false,
          field: `wp_ltd_start`,
          valueFormatter: this.currencyFormatter,
          aggFunc: Utils.agSumFunc,
          hide: true,
          cellClass: this.getCellClass(),
        },
        {
          headerName: 'Invoiced LTD',
          maxWidth: 150,
          minWidth: 150,
          resizable: false,
          hide: true,
          field: `invoice_ltd_start`,
          valueFormatter: this.currencyFormatter,
          aggFunc: Utils.agSumFunc,
          cellClass: this.getCellClassForInvoiceCells(),
          onCellClicked: (event) => {
            if (event.node.group || event.rowPinned === 'bottom') {
              return this.navigateByInvoices(event);
            }
          },
        },
        {
          headerName: 'Accrual LTD',
          maxWidth: 150,
          minWidth: 150,
          resizable: false,
          field: `starting_accrual`,
          valueFormatter: this.currencyFormatter,
          aggFunc: Utils.agSumFunc,
          cellClass: this.getCellClass(),
        },
        {
          headerName: 'Prepaid LTD',
          maxWidth: 150,
          minWidth: 150,
          resizable: false,
          field: `starting_prepaid`,
          valueFormatter: this.currencyFormatter,
          aggFunc: Utils.agSumFunc,
          cellClass: this.getCellClass(),
        },
      ],
    },
  ];

  gridOptions$ = new BehaviorSubject<GridOptions>({
    tooltipShowDelay: 500,
    defaultColDef: {
      sortable: false,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
      minWidth: 70,
      cellRenderer: AgCellWrapperComponent,
    },
    getRowStyle: (param: RowClassParams): any => {
      if (param.node.rowPinned) {
        return { 'font-weight': 'bold' };
      }
      return undefined;
    },
    initialGroupOrderComparator: (params) => {
      const a = this.organizationQuery.getEntity(params.nodeA.key)?.name || 0;
      const b = this.organizationQuery.getEntity(params.nodeB.key)?.name || 0;
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    },
    suppressMenuHide: true,
    headerHeight: 40,
    suppressCellFocus: true,
    groupDefaultExpanded: 0,
    suppressAggFuncInHeader: true,
    suppressRowHoverHighlight: false,
    rowSelection: 'single',
    groupSelectsChildren: false,
    columnDefs: [],
    excelStyles: [
      ...Utils.generateExcelCurrencyStyles(Utils.CURRENCY_OPTIONS),
      ...Utils.auxExcelStyle,
      {
        ...Utils.auxExcelStyle.find(({ id }) => id === 'first_row'),
        id: 'trial_name',
        borders: {
          borderBottom: {
            color: 'black',
            lineStyle: 'Continuous',
            weight: 1,
          },
        },
      },
    ],
  });

  budgetInfo$ = this.organizationQuery.selectActiveId().pipe(
    switchMap((org_id) =>
      this.budgetQuery.select('budget_info').pipe(
        map((x) => {
          return x.find((y) => y.vendor_id === org_id);
        })
      )
    )
  );

  showUnpaidColumns$ = new BehaviorSubject(false);

  constructor(
    public periodCloseComponent: PeriodCloseComponent,
    private periodCloseService: QuarterCloseChecklistPeriodCloseService,
    private overlayService: OverlayService,
    private apiService: ApiService,
    private budgetService: BudgetService,
    private budgetQuery: BudgetQuery,
    private mainQuery: MainQuery,
    private trialsQuery: TrialsQuery,
    private organizationQuery: OrganizationQuery,
    private authService: AuthService,
    private mainStore: MainStore,
    private authQuery: AuthQuery,
    private gqlService: GqlService,
    private workflowQuery: WorkflowQuery,
    private launchDarklyService: LaunchDarklyService,
    private documentLibraryService: DocumentLibraryService,
    private stickyElementService: StickyElementService,
    private invoiceService: InvoiceService,
    private router: Router
  ) {
    this.periodCloseComponent.selectedMonth$.next(null);
    this.periodCloseService.selectedQuarterMonthChanged$.next();

    combineLatest([this.mainQuery.select('trialKey'), this.gridAPI$.pipe(take(1), startWith(null))])
      .pipe(
        switchMap(() =>
          this.launchDarklyService.select$((flags) => flags.unpaid_invoices_ltd_column)
        )
      )
      .pipe(untilDestroyed(this))
      .subscribe((bool) => {
        this.showUnpaidColumns$.next(bool);
      });

    this.showRequestJournalEntryButton$ = this.launchDarklyService.select$(
      (flags) => flags.request_journal_entry_report_button
    );
  }

  ngOnInit() {
    this.invoiceService.initialize();
    this.gridAPI$
      .pipe(
        take(1),
        switchMap(() => this.periodCloseComponent.gridDataMergedDiscount$)
      )
      .pipe(untilDestroyed(this))
      .subscribe((mergedData) => {
        this.gridDataForFilter$.next(mergedData);
        this.updateGridOptions(mergedData);
      });
  }

  ngOnDestroy(): void {
    this.stickyElementService.reset();
  }

  onAddVendorEstimateUploadClick = () => {
    const overlay = this.overlayService.open({ content: AddVendorEstimateUploadComponent });
    overlay.afterClosed$.subscribe(() => this.refreshDownload$.next());
  };

  onAddPoReportUploadClick = () => {
    const overlay = this.overlayService.open({ content: AddPoReportUploadComponent });
    overlay.afterClosed$.subscribe(() => this.refreshDownload$.next());
  };

  getDynamicExcelParams = (): ExcelExportParams => {
    const trial = this.trialsQuery.getEntity(this.mainQuery.getValue().trialKey);
    if (!trial) {
      return {};
    }

    const totals = this.gridAPI.getPinnedBottomRow(0)?.data;

    let columnKeys = this.getDashboardIDs();

    const attributes = this.gridColumnApi
      ?.getAllColumns()
      ?.map((x) => [x.getColId(), x] as [string, Column])
      .filter(([x]: [string, Column]) => {
        return x.startsWith('custom_attr_') || ['account', 'po', 'dept'].includes(x);
      })
      .map(([x, c]) => {
        return [c.isVisible(), x] as [boolean, string];
      })!;

    const shownAttributes = attributes.filter(([bool]) => bool).length;

    const appendContent: ExcelExportParams['appendContent'] = [
      {
        cells: [
          {
            data: { value: `Total`, type: 'String' },
            mergeAcross: shownAttributes ? attributes.length + 1 : 1,
            styleId: 'total_row_header',
          },
        ],
      },
    ];

    if (totals) {
      const quarter_start_columns: (keyof QuarterStartDataForBottomRow)[] = [
        'wp_ltd_start',
        'invoice_ltd_start',
        'starting_accrual',
        'starting_prepaid',
      ];
      for (const column of quarter_start_columns) {
        appendContent[0].cells.push({
          data: { value: !Number.isNaN(totals[column]) ? totals[column] : 0, type: 'Number' },
          styleId: `total_row_${totals?.currency}`,
        });
      }
    }

    const pushTotalValue = (field: string) => {
      appendContent[0].cells.push({
        data: { value: get(totals, field) || 0, type: 'Number' },
        styleId: `total_row_${totals?.currency}`,
      });
    };

    this.periodCloseComponent.quartersObj[
      this.periodCloseComponent.selectedQuarter.value || ''
    ].forEach((month) => {
      this.periodCloseComponent.columnsForMonth.forEach((field) => {
        if (field === 'eom_accruals') {
          return;
        }
        pushTotalValue(`${field}.${month.date}`);
      });
    });

    const summary_columns: (keyof QuarterSummaryData)[] = [
      'accrual_adjusted_obj',
      'invoice_amounts',
      'net_accruals',
      'wp_ltd',
      `invoice_ltd`,
      'eom_accrual',
      'eom_prepaid',
      'eom_unpaid',
    ];

    for (const column of summary_columns) {
      if (column !== 'wp_ltd' && column !== `invoice_ltd`) {
        pushTotalValue(`${column}.total`);
      } else {
        pushTotalValue(`${column}_end`);
      }
    }

    const selectQ = this.periodCloseComponent.selectedQuarter.value || '';

    columnKeys = shownAttributes
      ? [
          columnKeys[shownAttributes],
          columnKeys[shownAttributes + 1],
          ...columnKeys.slice(0, shownAttributes),
          ...columnKeys.splice(shownAttributes + 2, columnKeys.length - 1),
        ]
      : [...columnKeys.splice(attributes.length)];

    const exportOptions = {
      ...this.excelOptions,
      fileName: `${trial.short_name}_${selectQ}.xlsx`,
      columnKeys,
      appendContent: totals ? appendContent : [],
      prependContent: [
        {
          cells: [
            {
              data: { value: `Trial: ${trial.short_name}`, type: 'String' },
              mergeAcross: columnKeys.length - 1,
              styleId: 'trial_name',
            },
          ],
        },
      ],
    } as ExcelExportParams;
    return exportOptions;
  };

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    this.stickyElementService.configure();
  }

  async onDownloadVendorEstimates() {
    const { trialKey } = this.mainQuery.getValue();

    const { success, data } = await this.apiService.getS3ZipFile(
      `trials/${trialKey}/vendors/vendor-estimate/${this.getCurrentMonthToClose()}`
    );
    if (success && data) {
      // [insert trial name]_[insert vendor name]_Change Order [insert Change Order #]_[insert date created YYYY.MM.DD]
      const fileName = `vendor-estimate`;
      await this.apiService.downloadZipOrFile(data, fileName);
    }
  }

  async onRequestJournalEntryReport() {
    const result = await this.gqlService
      .processEvent$({
        type: EventType.REQUEST_JOURNAL_ENTRY_REPORT_NOTIFICATION,
        entity_type: EntityType.DOCUMENT,
        entity_id: '',
        payload: JSON.stringify({
          current_open_month: this.getCurrentMonthToClose().replace('-', ' '),
        }),
      })
      .toPromise();
    if (result.success) {
      this.overlayService.success('Request for Journal Entry Report successfully processed');
    } else {
      this.overlayService.error('Error Requesting Journal Entry Report');
    }
  }

  async exportExcelToS3() {
    const additionalExcelOption = this.getDynamicExcelParams();
    const defaultExcelOptions: ExcelExportParams = {
      author: 'Auxilius',
      fontSize: 11,
      columnWidth: 105,
    };

    const exportedData = this.gridAPI?.getDataAsExcel({
      ...defaultExcelOptions,
      columnKeys: this.getDashboardIDs(),
      ...this.excelOptions,
      ...additionalExcelOption,
    });

    const month = dayjs(this.getCurrentMonthToClose());
    //formatted per req's for month close reports (the only place used)
    const fileFormattedDate = month.format('MMM-YYYY');

    const { trialKey } = this.mainQuery.getValue();
    const fileName = `${fileFormattedDate}_Accrual_Summary.xlsx`;
    const key = `trials/${trialKey}/period-close/${fileName}`;

    const user = await this.authService.getLoggedInUser();

    if (exportedData) {
      const fileToUpload = new File([exportedData], fileName);
      await this.apiService.uploadFile(key, fileToUpload, {
        uploadedBy: user?.getSub() || '',
      });

      this.documentLibraryService.uploadDocument(
        {
          id: Utils.uuid(),
          uploaded: false,
          eTag: Utils.uuid(),
          key,
          fileName: fileName,
          lastModified: now(),
          size: fileToUpload.size,
          type: 'csv',
        },
        {
          documentType: DocumentType.DOCUMENT_PERIOD_CLOSE,
          target_date: month.format('YYYY-MM-DD'),
        },
        key
      );
    }
  }

  isSelectedMonthEndOfQuarter = (date: dayjs.Dayjs) => {
    return dayjs(date).month() % 3 === 2;
  };

  navigateByInvoices(event: CellClickedEvent) {
    let navigateParams: { vendor_id: string; acc_months: Option[] } = {
      vendor_id: '',
      acc_months: [],
    };

    const columnId = event.column.getColId();
    const isTotalRow = event.rowPinned === 'bottom';

    const inQStart = columnId.includes('start');
    const inQSummary = columnId.includes('end') || columnId.includes('total');
    const inQMonth = !inQStart && !inQSummary;

    const vendorId = event.data ? event.data.vendor_id : event.node.key;

    const all_options = this.invoiceService.accrualPeriodOptions;
    const [qtr, year] = this.periodCloseComponent.selectedQuarter.value?.split('-') || [];

    if (!all_options || !qtr || !year) {
      return;
    }

    const selectedQtrDate = new Date(+year, +qtr.slice(1) * 3 - 3, 1);

    navigateParams.vendor_id = isTotalRow ? '' : vendorId;

    if (inQMonth) {
      const isInvoiceReceived = columnId.includes('amount');
      const isInvoiceLtd = columnId.includes('ltd');
      const [colMonth, colYear] = columnId.split('.').reverse().shift()?.split('-') || [];
      if (isInvoiceReceived) {
        const acc_option = this.getAccrualPeriodOptionsForMonths(colMonth, colYear, all_options)
          .acc_option;
        navigateParams.acc_months = [];
        navigateParams.acc_months.push(acc_option);
      }
      if (isInvoiceLtd && !isInvoiceReceived) {
        navigateParams.acc_months = this.getAccrualPeriodOptionsForMonths(
          colMonth,
          colYear,
          all_options
        ).before_and_current_options;
      }
    } else if (inQStart) {
      navigateParams.acc_months = this.getAccrualPeriodOptionsForQStart(
        all_options,
        selectedQtrDate
      ).before_acc_options;
    } else if (inQSummary) {
      const isInvoiceReceived = columnId.includes('amount');
      const isInvoiceLtd = columnId.includes('ltd');
      if (isInvoiceReceived) {
        navigateParams.acc_months = this.getAccrualPeriodOptionsForQSummary(
          all_options,
          selectedQtrDate
        ).quarter_acc_options;
      }
      if (isInvoiceLtd && !isInvoiceReceived) {
        navigateParams.acc_months = this.getAccrualPeriodOptionsForQSummary(
          all_options,
          selectedQtrDate
        ).before_and_quarter_acc_options;
      }
    }

    let acc_mont_values: string[] = [];

    navigateParams.acc_months.forEach((month) => {
      acc_mont_values.push(month.value);
    });

    this.invoiceService.setAccrualPeriodsAndVendorFilter(acc_mont_values, navigateParams.vendor_id);

    this.router.navigateByUrl(
      `/${ROUTING_PATH.VENDOR_PAYMENTS.INDEX}/${ROUTING_PATH.VENDOR_PAYMENTS.INVOICES}`
    );
  }

  getAccrualPeriodOptionsForQStart(all_options: Option[], date: Date) {
    const before_acc_options = all_options.filter((option) =>
      dayjs(`${option.value}-01`).isBefore(date)
    );
    return { before_acc_options };
  }

  getAccrualPeriodOptionsForQSummary(all_options: Option[], date: Date) {
    const quarter_acc_options = all_options.filter(
      (option) =>
        dayjs(`${option.value}-01`).quarter() === dayjs(date).quarter() &&
        dayjs(`${option.value}-01`).year() === dayjs(date).year()
    );
    const before_and_quarter_acc_options = all_options.filter(
      (option) =>
        (dayjs(`${option.value}-01`).quarter() === dayjs(date).quarter() &&
          dayjs(`${option.value}-01`).year() === dayjs(date).year()) ||
        dayjs(`${option.value}-01`).isBefore(date)
    );
    return { quarter_acc_options, before_and_quarter_acc_options };
  }

  getAccrualPeriodOptionsForMonths(month_short_name: string, year: string, all_options: Option[]) {
    let acc_option: Option = { label: '', value: '' };

    const shortMonthNames = Utils.SHORT_MONTH_NAMES;
    const longMonthNames = Utils.MONTH_NAMES;
    const monthIndex = shortMonthNames.findIndex(
      (month) => month.toUpperCase() === month_short_name
    );
    const longMonthName = longMonthNames[monthIndex];
    const monthNumber = monthIndex < 9 ? `0${monthIndex + 1}` : `${monthIndex + 1}`;
    acc_option.label = `${longMonthName} ${year}`;
    acc_option.value = `${year}-${monthNumber}`;

    const date = new Date(+year, monthIndex, 1);

    const before_and_current_options = all_options.filter((option) =>
      dayjs(`${option.value}-01`).isSameOrBefore(date)
    );

    return { acc_option, before_and_current_options };
  }

  async onInMonthClose() {
    const info = this.budgetQuery.getValue().budget_info;

    const first_vendor_current_month = info[0]?.current_month;
    if (!first_vendor_current_month) {
      return;
    }
    const formatted_current_month = formatDate(first_vendor_current_month, 'MMMM y', 'en-US');

    const resp = await this.overlayService
      .open<{ note: string; sendToNetsuite?: boolean } | undefined, any>({
        content: QuarterCloseDialogComponent,
        data: {
          header: `Close ${formatted_current_month}`,
          useDesignSystemStyling: true,
          displayX: true,
          formatted_current_month,
        },
      })
      .afterClosed$.toPromise();

    if (!resp.data?.note) {
      return;
    }

    await this.overlayService
      .open({
        content: QuarterCloseProcessingConfirmationComponent,
        data: {
          customHeader: QuarterCloseProcessingConfirmationHeaderComponent,
          useDesignSystemStyling: true,
        },
      })
      .afterClosed$.toPromise();

    const ref = this.overlayService.loading();

    await this.exportExcelToS3();

    const { current_month } = info[0];

    this.mainStore.setProcessingLoadingState(EventType.CLOSE_TRIAL_MONTH, true);

    const result = await this.gqlService
      .processEvent$({
        entity_id: '',
        entity_type: EntityType.TRIAL,
        type: EventType.CLOSE_TRIAL_MONTH,
        payload: JSON.stringify({
          event_dates: [current_month],
          sendToNetsuite: resp.data?.sendToNetsuite,
        }),
      })
      .toPromise();

    ref.close();

    if (result.success) {
      this.periodCloseComponent.monthClosed$.next(true);
      // this.overlayService.success('Month Successfully Closed');
      console.log('Month Successfully Closed');
    } else {
      const allErrors: string[] = [];
      allErrors.push(...result.errors);
      this.overlayService.error(allErrors);
    }
  }

  async onCellValueChanged(event: CellValueChangedEvent) {
    const loading = this.overlayService.loading();
    const user = await this.authService.getLoggedInUser();
    const id = event.column.getColId();
    const [, permission_type] = id.split('::');
    const [node] = this.gridAPI.getSelectedNodes();

    const { success, errors } = await this.gqlService
      .approveRule$({
        approved: true,
        comments: '',
        permission: permission_type,
        approval_type: ApprovalType.APPROVAL_IN_MONTH,
        entity_id: node.data.vendor_id,
        entity_type: EntityType.TRIALEVENT,
        activity_details: JSON.stringify({
          category: node.data.activity_name,
        }),
      })
      .toPromise();

    if (success) {
      if (user?.IsSysAdmin()) {
        node.setData({ ...node.data, [`${id}::disabled`]: true });
      } else {
        const columnsToChange = ((this.gridColumnApi.getColumns() || []) as Column[]).filter((x) =>
          x.getColId().includes(permission_type)
        );

        columnsToChange.forEach((col) => {
          node.setData({ ...node.data, [`${col.getColId()}::disabled`]: true });
        });
      }

      this.overlayService.success('Approved!');
    } else {
      node.setData({ ...node.data, [id]: false });
      this.overlayService.error(errors);
    }

    loading.close();
  }

  onGridReady({ api, columnApi }: GridReadyEvent) {
    this.gridAPI = api;
    this.gridAPI$.next(api);
    this.gridColumnApi = columnApi;
    this.gridColumnApi$.next(columnApi);

    const allColumnIds: string[] = [];
    (columnApi.getColumns() || []).forEach((column: Column) => {
      allColumnIds.push(column.getColId());
    });

    columnApi.autoSizeColumns(allColumnIds, false);

    const obj = this.periodCloseComponent.bottomRowDataForMergedDiscount$.getValue();
    // period-close will set all bottomRowData objects empty if there are multiple currencies
    if (
      isEmpty(obj.eom_accruals) &&
      isEmpty(obj.net_accruals) &&
      isEmpty(obj.monthly_wp_obj) &&
      isEmpty(obj.initial_wp_obj)
    ) {
      this.gridAPI.setPinnedBottomRowData([]);
    } else {
      this.gridAPI.setPinnedBottomRowData([
        merge(
          {
            activity_name: 'Total',
          },
          obj
        ),
      ]);
    }
  }

  getCurrentMonthToClose() {
    const info = this.budgetQuery.getValue().budget_info;
    const first_vendor_current_month = info[0]?.current_month;
    return formatDate(first_vendor_current_month || '', 'MMMM-y', 'en-US');
  }

  private getMonthColumn(
    headerName: string,
    isSame: boolean,
    isFuture: boolean,
    rowKey: string,
    month: QuarterDate | null,
    hideTotal?: boolean,
    readOnlyAdjustment?: boolean,
    isSummary = false
  ) {
    const adjustmentColumnProps = !readOnlyAdjustment
      ? {
          cellRenderer: AgQuarterCloseAdjustmentComponent,
          cellRendererParams: {
            onClicked: (event: ICellRendererParams) => {
              this.periodCloseComponent.onSelectionChanged(event);
            },
            current_month: isSame,
            adjustmentWorkflow$: this.adjustmentWorkflow$,
            isWorkflowAvailable$: this.isWorkflowAvailable$,
            userHasAdjustPermission$: this.userHasAdjustPermission$,
          },
          cellClass: ['cost', '!p-0'],
        }
      : {
          cellClass: ['cost'],
        };

    const getMoneyColumn = () => {
      return {
        maxWidth: 150,
        minWidth: 150,
        resizable: false,
        valueFormatter: this.currencyFormatter,
        aggFunc: Utils.agSumFunc,
        cellClass: this.getCellClass(),
        columnGroupShow: (isSame ? 'closed' : 'open') as ColumnGroupShowType,
      };
    };
    const debit_credit_columns: ColDef[] = isSummary
      ? []
      : [
          {
            ...getMoneyColumn(),
            headerName: 'Accrual Debit',
            hide: true,
            field: `eom_accrual_debit.${rowKey}`,
          },
          {
            ...getMoneyColumn(),
            headerName: 'Accrual Credit',
            hide: true,
            field: `eom_accrual_credit.${rowKey}`,
          },
          {
            ...getMoneyColumn(),
            headerName: 'Prepaid Debit',
            hide: true,
            field: `eom_prepaid_debit.${rowKey}`,
          },
          {
            ...getMoneyColumn(),
            headerName: 'Prepaid Credit',
            hide: true,
            field: `eom_prepaid_credit.${rowKey}`,
          },
        ];
    const children: ColDef[] = [
      {
        ...getMoneyColumn(),
        ...adjustmentColumnProps,
        headerName: isSummary ? 'Quarterly WP Estimate' : 'Monthly WP Estimate',
        field: `accrual_adjusted_obj.${rowKey}`,
        cellClass: this.getCellClass(),
      },
      {
        ...getMoneyColumn(),
        headerName: isSummary ? 'Quarterly Invoices Received' : 'Monthly Invoices Received',
        field: `invoice_amounts.${rowKey}`,
        cellClass: this.getCellClassForInvoiceCells(),
        onCellClicked: (event) => {
          if (event.node.group || event.rowPinned === 'bottom') {
            return this.navigateByInvoices(event);
          }
        },
      },
      {
        headerComponent: AgQuarterCloseCollapsableHeaderComponent,
        headerComponentParams: {
          template: `Net Change in <br/> Accruals / (Prepaid)`,
          columns: isSummary
            ? []
            : [
                `eom_accrual_debit.${rowKey}`,
                `eom_accrual_credit.${rowKey}`,
                `eom_prepaid_debit.${rowKey}`,
                `eom_prepaid_credit.${rowKey}`,
              ],
        } as AgQuarterCloseCollapsableHeaderParams,
        headerName: 'Net Change in Accruals / (Prepaid)',
        maxWidth: 210,
        minWidth: 210,
        resizable: false,
        field: `net_accruals.${rowKey}`,
        columnGroupShow: isSame ? 'closed' : 'open',
        valueFormatter: this.currencyFormatter,
        aggFunc: Utils.agSumFunc,
        cellClass: this.getCellClass(),
        headerClass: 'net_accruals_header',
      },
      ...debit_credit_columns,
      {
        ...getMoneyColumn(),
        headerName: 'WP LTD',
        field: isSummary ? `wp_ltd_end` : `monthly_wp_obj.${rowKey}`,
      },
      {
        ...getMoneyColumn(),
        headerName: 'Invoiced LTD',
        onCellClicked: (event) => {
          if (event.node.group || event.rowPinned === 'bottom') {
            return this.navigateByInvoices(event);
          }
        },
        cellClass: this.getCellClassForInvoiceCells(),
        field: isSummary ? `invoice_ltd_end` : `monthly_invoice_ltd.${rowKey}`,
      },
      {
        minWidth: 150,
        width: 150,
        resizable: false,
        valueFormatter: this.currencyFormatter,
        aggFunc: Utils.agSumFunc,
        cellClass: this.getCellClass(),
        // columnGroupShow: isSame ? 'closed' : 'open',
        headerName: 'Accrual LTD',
        columnGroupShow: undefined,
        field: `eom_accrual.${rowKey}`,
      },
      {
        minWidth: 150,
        width: 150,
        resizable: false,
        valueFormatter: this.currencyFormatter,
        aggFunc: Utils.agSumFunc,
        cellClass: this.getCellClass(),
        // columnGroupShow: isSame ? 'closed' : 'open',
        headerName: 'Prepaid LTD',
        columnGroupShow: undefined,
        field: `eom_prepaid.${rowKey}`,
      },
      {
        ...getMoneyColumn(),
        headerName: 'Unpaid Invoices LTD',
        field: isSummary ? `invoice_unpaid_ltd_end` : `eom_unpaid.${rowKey}`,
        hide: !this.showUnpaidColumns$.getValue(),
      },
    ];
    return {
      headerName,
      headerClass: isSame ? 'open gray-dark-header-group' : 'closed',
      headerGroupComponent: AgInMonthGroupHeaderComponent,
      headerGroupComponentParams: {
        processing$: this.periodCloseComponent.iCloseMonthsProcessing$,
        isMonthCloseAvailable$: this.isCloseMonthAvailable$,
        isQuarterCloseEnabled$: this.isQuarterCloseEnabled$,
        isWorkflowAvailable$: this.isWorkflowAvailable$,
        userHasClosingPermission$: this.userHasClosingPermission$,
        bottomRowData$: this.periodCloseComponent.bottomRowData$,
        budgetInfo$: this.budgetInfo$,
        // eslint-disable-next-line no-nested-ternary
        mode: isFuture ? 'future' : isSame ? 'open' : 'closed',
        month,
        hideTotal,
        headerName,
        monthButtonClick: () => {
          if (isSame) {
            this.onInMonthClose();
          }
        },
      },
      minWidth: 200,
      children,
    };
  }

  private isColGroup(cell: ColDef | ColGroupDef): cell is ColGroupDef {
    return !!(cell as ColGroupDef)?.children;
  }

  private isColDef(col: ColDef | ColGroupDef): col is ColDef {
    return !!(col as ColDef).colId;
  }

  private getChildrenGroupIds(cols: (ColDef | ColGroupDef)[]): string[] {
    return cols.filter(this.isColDef).map((col) => col.colId) as string[];
  }

  private getDashboardIDs(): string[] {
    if (!this.gridAPI) {
      return [];
    }

    return (this.gridAPI?.getColumnDefs() || [])
      .filter((cel) => !!cel.headerName)
      .reduce<string[]>((accum, cell: ColDef | ColGroupDef) => {
        if (this.isColGroup(cell)) {
          const res = this.getChildrenGroupIds(cell.children);
          // eslint-disable-next-line no-param-reassign
          accum = accum.concat(res || []);
        }

        if (this.isColDef(cell)) {
          accum.push(cell.colId || '');
        }

        return accum;
      }, []);
  }

  onFilterChanged(e: FilterChangedEvent) {
    let notFilteredParentRowAndChilds: {
      whichVendor: string;
      whichChilds: string[];
    }[] = [];

    e.api.forEachNodeAfterFilter((row) => {
      if (row.group) {
        const key = row.key || '';
        let whichChilds: string[] = [];
        row.childrenAfterFilter?.forEach((children) => {
          whichChilds.push(children.data.activity_name);
        });
        notFilteredParentRowAndChilds.push({ whichVendor: key, whichChilds: whichChilds });
      }
    });

    const changedData: MonthCloseTableRowData[] = this.gridDataForFilter$
      .getValue()
      .filter((rowData) => {
        const fakeData = rowData as any;
        return notFilteredParentRowAndChilds.some(
          (rowGroup) =>
            rowGroup.whichVendor === fakeData.vendor_id &&
            rowGroup.whichChilds.includes(fakeData.activity_name)
        );
      });

    let { wp_ltd_pinned, invoice_ltd_pinned, starting_accrual_pinned, starting_prepaid_pinned } = {
      wp_ltd_pinned: 0,
      invoice_ltd_pinned: 0,
      starting_accrual_pinned: 0,
      starting_prepaid_pinned: 0,
    };

    let {
      ending_accruals_pinned,
      wp_ltd_end_pinned,
      invoice_ltd_end_pinned,
      invoice_unpaid_ltd_end_pinned,
    } = {
      ending_accruals_pinned: 0,
      wp_ltd_end_pinned: 0,
      invoice_ltd_end_pinned: 0,
      invoice_unpaid_ltd_end_pinned: 0,
    };

    // generatePinnedBottomData function takes the parameters below from directly quarter start datat and quarter end data,
    // so i should calculate these variables seperately. The other variables are filtered inside the function.
    changedData.forEach((data) => {
      const dataAsAny = data as any;
      wp_ltd_pinned += dataAsAny.wp_ltd;
      invoice_ltd_pinned += dataAsAny.invoice_ltd;
      starting_accrual_pinned += dataAsAny.starting_accrual;
      starting_prepaid_pinned += dataAsAny.starting_prepaid;
      ending_accruals_pinned += dataAsAny.ending_accruals;
      wp_ltd_end_pinned += dataAsAny.wp_ltd_end;
      invoice_ltd_end_pinned += dataAsAny.invoice_ltd_end;
      invoice_unpaid_ltd_end_pinned += dataAsAny.invoice_unpaid_ltd_end;
    });

    const { obj } = this.periodCloseComponent.generatePinnedBottomData(
      changedData as any,
      this.periodCloseComponent.currencies.values().next().value
    );

    const finalObj = {
      ...obj,
      wp_ltd_start: wp_ltd_pinned,
      wp_ltd_end: wp_ltd_end_pinned,
      starting_accrual: starting_accrual_pinned,
      starting_prepaid: starting_prepaid_pinned,
      invoice_ltd_start: invoice_ltd_pinned,
      invoice_ltd_end: invoice_ltd_end_pinned,
      invoice_unpaid_ltd_end: invoice_unpaid_ltd_end_pinned,
      ending_accruals: ending_accruals_pinned,
    };

    // period-close will set all bottomRowData objects empty if there are multiple currencies
    if (
      isEmpty(finalObj.eom_accruals) &&
      isEmpty(finalObj.net_accruals) &&
      isEmpty(finalObj.monthly_wp_obj) &&
      isEmpty(finalObj.initial_wp_obj)
    ) {
      this.gridAPI.setPinnedBottomRowData([]);
    } else {
      this.gridAPI.setPinnedBottomRowData([
        merge(
          {
            activity_name: 'Total',
          },
          finalObj
        ),
      ]);
    }
  }

  updateGridOptions(mergedData: MonthCloseTableRowData[]) {
    this.showGrid$.next(false);

    let columns = [];

    const attributes = calcColumns({
      attributes: mergedData.map((z) => z.attributes!)!,
    });

    const attr = attributeColumnDef(attributes, QCAttributesLSKey);
    attr.headerClass = ['future', 'ag-header-align-center'];
    columns = [attr, ...this.defaultColumns];

    const addEmptyColumn = () => {
      columns.push(TableConstants.SPACER_COLUMN);
    };
    (
      this.periodCloseComponent.quartersObj[
        this.periodCloseComponent.selectedQuarter.value || ''
      ] || []
    ).forEach((month) => {
      const isAfter = dayjs(this.periodCloseComponent.currentMonth)
        .date(1)
        .isAfter(dayjs(month.iso).date(1));
      const isSame = dayjs(this.periodCloseComponent.currentMonth)
        .date(1)
        .isSame(dayjs(month.iso).date(1));
      const isFuture = !(isSame || isAfter);
      addEmptyColumn();

      const slicedDate = month.iso.slice(0, 7);

      const monthColumn = this.getMonthColumn(slicedDate, isSame, isFuture, month.date, month);

      columns.push(monthColumn);
    });
    addEmptyColumn();

    columns.push(
      this.getMonthColumn(
        `${this.periodCloseComponent.selectedQuarter.value?.replace('-', ' ') || ''} Summary`,
        false,
        true,
        'total',
        null,
        true,
        true,
        true
      )
    );

    // Quarter end part, did not delete it incase it would be considered to use again.
    // columns.push(
    //   ...[
    //     TableConstants.SPACER_COLUMN,
    //     {
    //       headerName: 'Quarter End',
    //       headerClass: ['future', 'ag-header-align-center'],
    //       headerGroupComponent: AgQuarterExpandableGroupHeaderComponent,
    //       children: [
    //         {
    //           headerName: 'Ending Accruals  (Prepaid)',
    //           maxWidth: 150,
    //           minWidth: 150,
    //           resizable: false,
    //           field: `ending_accruals`,
    //           valueFormatter: this.currencyFormatter,
    //           aggFunc: Utils.agSumFunc,
    //           cellClass: this.getCellClass(),
    //         },
    //       ],
    //     },
    //   ]
    // );

    this.gridOptions$.next({
      ...this.gridOptions$.getValue(),
      columnDefs: [...columns],
      autoGroupColumnDef: {
        ...this.autoAttributeGroupColumnDef,
        headerComponentParams: {
          ...this.autoAttributeGroupColumnDef.headerComponentParams,
          columnsToCollapse: attr.children.map((x: ColDef) => x.colId || x.field),
        },
      },
    });

    setTimeout(() => {
      this.showGrid$.next(true);
    }, 0);
  }

  refreshGridWidth() {
    const div = document.querySelector('.ag-header-container') as HTMLDivElement;
    if (div) {
      const paddingOffset = 2;
      this.width$.next(div.getBoundingClientRect().width + paddingOffset);
    } else {
      this.width$.next(0);
    }
  }

  agCurrencyFormatter(val: ValueFormatterParams) {
    if (val.value) {
      if (!Number.isNaN(val.value)) {
        return Utils.currencyFormatter(val.value);
      }
    }

    return Utils.zeroHyphen;
  }

  onFirstDataRendered({ api }: FirstDataRenderedEvent) {
    const rowNodes: IRowNode[] = [];
    api.forEachNode((rowNode) => rowNodes.push(rowNode));
    first(rowNodes)?.setSelected(true, true);

    // this.refreshGridWidth();
  }

  async onOpenDialog(category: FullCategory, sub_activity: FullActivity, preview_mode = false) {
    const budget_info = await this.budgetInfo$.pipe(take(1)).toPromise();
    const resp = await this.overlayService
      .open({
        content: QuarterCloseManualOverrideComponent,
        data: { sub_activity, budget_info, preview_mode },
      })
      .afterClosed$.toPromise();

    if (resp.data) {
      this.periodCloseComponent.refresh$.next();
    }
  }

  onVendorEstimateUploadClick() {
    this.overlayService.open({ content: VendorEstimateUploadComponent });
  }

  refreshRows(): void {
    this.gridAPI?.redrawRows();
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    this.stickyElementService.configure();
  }
}
